import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboardIcon, BarChartIcon, QrCodeIcon, FileTextIcon, UserIcon, MegaphoneIcon, SettingsIcon, BuildingIcon, LogOutIcon, MenuIcon, XIcon, BellIcon, HelpCircleIcon, SearchIcon, SunIcon, MoonIcon, ActivityIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
const MerchantPortal: React.FC = () => {
  const {
    user,
    logout
  } = useAuth();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Mock merchant data
  const merchantInfo = {
    name: 'Global Electronics',
    tier: 'Premium',
    logo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OHx8Y29tcGFueSUyMGxvZ298ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=100&q=60',
    unreadNotifications: 3,
    isMultiMerchant: true,
    subsidiaries: [{
      id: 'sub1',
      name: 'Downtown Electronics'
    }, {
      id: 'sub2',
      name: 'City Tech Store'
    }, {
      id: 'sub3',
      name: 'Metro Gadgets'
    }],
    activeMerchant: 'Global Electronics'
  };
  const navigationItems = [{
    name: 'Dashboard',
    icon: <LayoutDashboardIcon className="h-5 w-5" data-id="element-4689" />,
    path: '/merchant-portal/dashboard'
  }, {
    name: 'Transactions',
    icon: <BarChartIcon className="h-5 w-5" data-id="element-4690" />,
    path: '/merchant-portal/transactions'
  }, {
    name: 'Generate QR',
    icon: <QrCodeIcon className="h-5 w-5" data-id="element-4691" />,
    path: '/merchant-portal/qr-generator'
  }, {
    name: 'Payment Monitor',
    icon: <ActivityIcon className="h-5 w-5" data-id="element-4692" />,
    path: '/merchant-portal/payment-monitor'
  }, {
    name: 'Reports',
    icon: <FileTextIcon className="h-5 w-5" data-id="element-4693" />,
    path: '/merchant-portal/reports'
  }, {
    name: 'Profile',
    icon: <UserIcon className="h-5 w-5" data-id="element-4694" />,
    path: '/merchant-portal/profile'
  }, {
    name: 'Campaigns',
    icon: <MegaphoneIcon className="h-5 w-5" data-id="element-4695" />,
    path: '/merchant-portal/campaigns'
  }, {
    name: 'Settings',
    icon: <SettingsIcon className="h-5 w-5" data-id="element-4696" />,
    path: '/merchant-portal/settings'
  }];
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  const handleMerchantChange = (merchantName: string) => {
    console.log(`Switched to merchant: ${merchantName}`);
    // In a real app, this would update the current merchant context
  };
  return <div className="flex h-screen bg-gray-50 dark:bg-gray-900" data-id="element-4697">
      {/* Mobile menu backdrop */}
      {mobileMenuOpen && <div className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden" onClick={() => setMobileMenuOpen(false)} data-id="element-4698"></div>}

      {/* Sidebar - Mobile */}
      <div className={`fixed inset-y-0 left-0 z-30 w-64 transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out lg:hidden`} data-id="element-4699">
        <div className="flex h-full flex-col bg-gradient-to-b from-purple-700 to-purple-900 text-white shadow-xl" data-id="element-4700">
          <div className="flex items-center justify-between px-4 py-5" data-id="element-4701">
            <div className="flex items-center space-x-3" data-id="element-4702">
              {merchantInfo.logo ? <img className="h-10 w-auto rounded-md" src={merchantInfo.logo} alt={merchantInfo.name} data-id="element-4703" /> : <div className="h-10 w-10 rounded-md bg-white flex items-center justify-center text-purple-800 text-xl font-bold" data-id="element-4704">
                  {merchantInfo.name.charAt(0)}
                </div>}
              <div data-id="element-4705">
                <h2 className="text-lg font-bold text-white" data-id="element-4706">
                  {merchantInfo.name}
                </h2>
                <p className="text-xs text-purple-200" data-id="element-4707">
                  {merchantInfo.tier} Tier
                </p>
              </div>
            </div>
            <button className="rounded-full p-1 text-purple-200 hover:bg-purple-800 hover:text-white" onClick={() => setMobileMenuOpen(false)} data-id="element-4708">
              <XIcon className="h-6 w-6" data-id="element-4709" />
            </button>
          </div>
          {merchantInfo.isMultiMerchant && <div className="px-4 py-3 border-b border-purple-600/50" data-id="element-4710">
              <label htmlFor="mobile-merchant-select" className="block text-xs font-medium text-purple-200 mb-1" data-id="element-4711">
                Active Merchant
              </label>
              <select id="mobile-merchant-select" className="block w-full pl-3 pr-10 py-2 text-sm border border-purple-600/50 rounded-md bg-purple-800/50 text-white focus:outline-none focus:ring-2 focus:ring-white" value={merchantInfo.activeMerchant} onChange={e => handleMerchantChange(e.target.value)} data-id="element-4712">
                <option value={merchantInfo.name} data-id="element-4713">{merchantInfo.name}</option>
                {merchantInfo.subsidiaries.map(sub => <option key={sub.id} value={sub.name} data-id="element-4714">
                    {sub.name}
                  </option>)}
              </select>
            </div>}
          <nav className="flex-1 overflow-y-auto px-4 py-4" data-id="element-4715">
            <ul className="space-y-1" data-id="element-4716">
              {navigationItems.map(item => <li key={item.name} data-id="element-4717">
                  <NavLink to={item.path} className={({
                isActive
              }) => `flex items-center rounded-lg px-4 py-3 text-sm font-medium ${isActive ? 'bg-white text-purple-800' : 'text-purple-100 hover:bg-purple-800/50'}`} onClick={() => setMobileMenuOpen(false)} data-id="element-4718">
                    <span className="mr-3" data-id="element-4719">{item.icon}</span>
                    <span data-id="element-4720">{item.name}</span>
                  </NavLink>
                </li>)}
            </ul>
          </nav>
          <div className="border-t border-purple-600/50 p-4" data-id="element-4721">
            <button onClick={logout} className="flex w-full items-center rounded-lg px-4 py-2 text-sm font-medium text-purple-100 hover:bg-purple-800/50" data-id="element-4722">
              <LogOutIcon className="mr-3 h-5 w-5" data-id="element-4723" />
              <span data-id="element-4724">Log out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sidebar - Desktop */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col" data-id="element-4725">
        <div className="flex h-full flex-col bg-gradient-to-b from-purple-700 to-purple-900 text-white shadow-xl" data-id="element-4726">
          <div className="flex items-center px-6 py-6 border-b border-purple-600/50" data-id="element-4727">
            {merchantInfo.logo ? <img className="h-10 w-auto rounded-md" src={merchantInfo.logo} alt={merchantInfo.name} data-id="element-4728" /> : <div className="h-10 w-10 rounded-md bg-white flex items-center justify-center text-purple-800 text-xl font-bold" data-id="element-4729">
                {merchantInfo.name.charAt(0)}
              </div>}
            <div className="ml-3" data-id="element-4730">
              <h2 className="text-lg font-bold text-white" data-id="element-4731">
                {merchantInfo.name}
              </h2>
              <p className="text-xs text-purple-200" data-id="element-4732">
                {merchantInfo.tier} Tier
              </p>
            </div>
          </div>
          {merchantInfo.isMultiMerchant && <div className="px-6 py-4 border-b border-purple-600/50" data-id="element-4733">
              <label htmlFor="merchant-select" className="block text-xs font-medium text-purple-200 mb-1" data-id="element-4734">
                Active Merchant
              </label>
              <select id="merchant-select" className="block w-full pl-3 pr-10 py-2 text-sm border border-purple-600/50 rounded-md bg-purple-800/50 text-white focus:outline-none focus:ring-2 focus:ring-white" value={merchantInfo.activeMerchant} onChange={e => handleMerchantChange(e.target.value)} data-id="element-4735">
                <option value={merchantInfo.name} data-id="element-4736">{merchantInfo.name}</option>
                {merchantInfo.subsidiaries.map(sub => <option key={sub.id} value={sub.name} data-id="element-4737">
                    {sub.name}
                  </option>)}
              </select>
            </div>}
          <nav className="flex-1 overflow-y-auto px-4 py-6" data-id="element-4738">
            <ul className="space-y-1.5" data-id="element-4739">
              {navigationItems.map(item => <li key={item.name} data-id="element-4740">
                  <NavLink to={item.path} className={({
                isActive
              }) => `flex items-center rounded-lg px-4 py-3 text-sm font-medium ${isActive ? 'bg-white text-purple-800' : 'text-purple-100 hover:bg-purple-800/50'}`} data-id="element-4741">
                    <span className="mr-3" data-id="element-4742">{item.icon}</span>
                    <span data-id="element-4743">{item.name}</span>
                  </NavLink>
                </li>)}
            </ul>
          </nav>
          <div className="border-t border-purple-600/50 p-4" data-id="element-4744">
            <button onClick={logout} className="flex w-full items-center rounded-lg px-4 py-2 text-sm font-medium text-purple-100 hover:bg-purple-800/50" data-id="element-4745">
              <LogOutIcon className="mr-3 h-5 w-5" data-id="element-4746" />
              <span data-id="element-4747">Log out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden" data-id="element-4748">
        {/* Top Navigation */}
        <header className="bg-white dark:bg-gray-800 shadow-sm z-10" data-id="element-4749">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6" data-id="element-4750">
            <div className="flex items-center" data-id="element-4751">
              <button type="button" className="text-gray-500 dark:text-gray-400 lg:hidden" onClick={() => setMobileMenuOpen(true)} data-id="element-4752">
                <MenuIcon className="h-6 w-6" data-id="element-4753" />
              </button>
              <div className="ml-4 lg:ml-0" data-id="element-4754">
                <h1 className="text-xl font-bold text-purple-700 dark:text-purple-400" data-id="element-4755">
                  Merchant Portal
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4" data-id="element-4756">
              {/* Search */}
              <div className="relative" data-id="element-4757">
                <button onClick={() => setSearchOpen(!searchOpen)} className="p-1 text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 focus:outline-none" data-id="element-4758">
                  <SearchIcon className="h-5 w-5" data-id="element-4759" />
                </button>
                {searchOpen && <div className="absolute right-0 mt-2 w-72 rounded-md bg-white dark:bg-gray-800 shadow-lg p-2 z-10" data-id="element-4760">
                    <div className="relative" data-id="element-4761">
                      <input type="text" placeholder="Search..." className="w-full rounded-md border border-gray-300 dark:border-gray-600 py-2 pl-3 pr-10 text-sm bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-4762" />
                      <SearchIcon className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" data-id="element-4763" />
                    </div>
                  </div>}
              </div>
              {/* Theme Toggle */}
              <button onClick={toggleTheme} className="p-1 text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 focus:outline-none" data-id="element-4764">
                {theme === 'dark' ? <SunIcon className="h-5 w-5" data-id="element-4765" /> : <MoonIcon className="h-5 w-5" data-id="element-4766" />}
              </button>
              {/* Help */}
              <button className="p-1 text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 focus:outline-none" data-id="element-4767">
                <HelpCircleIcon className="h-5 w-5" data-id="element-4768" />
              </button>
              {/* Notifications */}
              <button className="p-1 text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 focus:outline-none" data-id="element-4769">
                <div className="relative" data-id="element-4770">
                  <BellIcon className="h-5 w-5" data-id="element-4771" />
                  {merchantInfo.unreadNotifications > 0 && <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-purple-600 text-xs font-bold text-white" data-id="element-4772">
                      {merchantInfo.unreadNotifications}
                    </span>}
                </div>
              </button>
              {/* User Profile */}
              <div className="flex items-center" data-id="element-4773">
                <div className="h-8 w-8 rounded-full bg-purple-600 flex items-center justify-center text-white font-medium" data-id="element-4774">
                  {user?.name.charAt(0)}
                </div>
                <div className="ml-3 hidden md:block" data-id="element-4775">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-200" data-id="element-4776">
                    {user?.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4777">
                    {user?.role}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 p-4 sm:p-6" data-id="element-4778">
          <div className="mx-auto max-w-7xl" data-id="element-4779">
            <Outlet data-id="element-4780" />
          </div>
        </main>
      </div>
    </div>;
};
export default MerchantPortal;